import { SfTable } from "@/components/core/sfTable";
import { PaneBody } from "@/components/pane/paneBody";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { IColumn } from "@/domain/interfaces/sfTable.interface";
import {
  IAlertHistory,
  useAlertHistoryStore,
} from "@/lib/stores/useAlertHistoryStore";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useState } from "react";
import dayjs from "dayjs";

const columnHelper = createColumnHelper<IAlertHistory>();

const columns = [
  columnHelper.accessor("type", {
    header: () => "Type",
    enableSorting: false,
    size: 70,
    minSize: 30,
  }),
  columnHelper.accessor("symbol", {
    header: () => "Symbol",
    enableSorting: false,
    size: 70,
    minSize: 30,
  }),
  columnHelper.accessor("message", {
    header: () => "Message",
    enableSorting: false,
    size: 70,
    minSize: 30,
  }),
  columnHelper.accessor("timestamp", {
    header: () => "Time",
    cell: (info) => `${dayjs(info.getValue()).fromNow()} ago`,
    enableSorting: true,
    size: 70,
    minSize: 30,
  }),
  columnHelper.display({
    id: "actions",
    cell: (info) => (
      <Button
        variant="ghost"
        size="xs"
        onClick={() => {
          useAlertHistoryStore.getState().removeAlert(info.row.original);
        }}
      >
        <i className="fa-regular text-red-400 fa-trash" />
      </Button>
    ),
    size: 30,
    minSize: 30,
  }),
];

export function AlertHistory() {
  const [alerts] = useAlertHistoryStore((state) => [state.alerts]);
  const [sorting, setSorting] = useState([{ id: "timestamp", desc: true }]);
  const table = useReactTable({
    data: alerts,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
    onSortingChange: setSorting,
  });

  return (
    <>
      <div className="flex justify-between items-center mb-2">
        <Button
          size="xs"
          onClick={() => {
            useAlertHistoryStore.getState().clearAlerts();
          }}
        >
          Clear
        </Button>
      </div>
      <Table>
        <TableHeader className="text-xs">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
}
